import { useId, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  Autocomplete,
  Button,
  DialogTrigger,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Select,
  SelectValue,
  Text,
} from '@launchpad-ui/components';
import type { ListBoxProps, SelectProps } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type { ContextKind } from '../../types/types';
import { ContextKindLabel } from '../ContextKindLabel/ContextKindLabel';

import { useListOverflow } from './useListOverflow';

import styles from './ContextKindSelector.module.css';

export type ContextKindSelectorProps = {
  /** The context kinds available to choose from. */
  contextKinds: ContextKind[];
  /** The currently selected context kind key(s). */
  selectedKeys?: string[];
  /** Called with the full new selection whenever it changes. */
  onSelectionChange?: (selectedKeys: string[]) => void;
  /** Visible label rendered above the trigger. */
  label?: string;
  /** Render a custom label element. Receives the trigger element's `id` so the label can set `htmlFor` to focus it. Takes precedence over `label`. */
  renderLabel?: (triggerId: string) => ReactNode;
  /** Accessible name for screen readers when no visible label is provided. */
  'aria-label'?: string;
  /** Trigger text shown when nothing is selected. */
  placeholder?: string;
  /** Show a search field above the list to filter by name or key. Useful for long lists. */
  isSearchable?: boolean;
  /**
   * When multiple values are selected, truncate long individual labels in the trigger
   * (matches `MultiSelectWithSearch`). Only affects the multi-select trigger text.
   */
  shortenLabels?: boolean;
  /** Whether the selector is in an invalid/error state. */
  isInvalid?: boolean;
  /** Prevent users from deselecting all items (multi-select only). */
  disallowEmptySelection?: boolean;
  className?: string;
} & Pick<SelectProps<object>, 'isDisabled'> &
  Pick<ListBoxProps<object>, 'selectionMode'>;

// Case-insensitive substring match against the item's text (name + key).
const filterByText = (textValue: string, inputValue: string) =>
  textValue.toLowerCase().includes(inputValue.trim().toLowerCase());

/**
 * The canonical popover for selecting one or more context kinds. Renders each kind as a
 * `ContextKindLabel` (name over code-styled key), and supports single- or multi-select via
 * `selectionMode`. Set `isSearchable` to filter long lists. Reuse this instead of building a
 * per-surface context kind dropdown.
 *
 * Layout follows the context kind selection menu (Figma node 2:12692).
 */
export const ContextKindSelector = ({
  contextKinds,
  selectionMode = 'single',
  selectedKeys,
  onSelectionChange,
  label,
  renderLabel,
  'aria-label': ariaLabel,
  placeholder = 'Select a context kind',
  isSearchable = false,
  shortenLabels = false,
  isInvalid,
  disallowEmptySelection,
  isDisabled,
  className,
}: ContextKindSelectorProps) => {
  const triggerId = useId();
  const kinds = contextKinds ?? [];
  const byKey = useMemo(() => new Map(kinds.map((ck) => [ck.key, ck])), [kinds]);
  const resolvedAriaLabel = ariaLabel ?? label ?? 'Context kinds';
  // Only offer search once the list actually overflows the dropdown; a list that
  // fits has nothing to filter. (Used by the single-select branch below; the
  // multi-select branch tracks its own list.)
  const { ref: listRef, overflows } = useListOverflow(kinds.length);
  const showSearch = isSearchable && overflows;

  const emptyState = (
    <div className={styles.empty}>{kinds.length > 0 ? 'No matching context kinds' : 'No context kinds'}</div>
  );

  const listBoxContent = (contextKind: ContextKind) => (
    <ListBoxItem
      id={contextKind.key}
      textValue={contextKind.hideKey ? contextKind.name : `${contextKind.name ?? ''} ${contextKind.key}`.trim()}
    >
      {contextKind.hideKey ? (
        <Text slot="label">{contextKind.name}</Text>
      ) : (
        <ContextKindLabel contextKind={contextKind} />
      )}
      {contextKind.description ? <Text slot="description">{contextKind.description}</Text> : null}
    </ListBoxItem>
  );

  if (selectionMode === 'multiple') {
    return (
      <MultiSelectContextKindSelector
        kinds={kinds}
        byKey={byKey}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        label={label}
        renderLabel={renderLabel}
        ariaLabel={resolvedAriaLabel}
        triggerId={triggerId}
        placeholder={placeholder}
        isSearchable={isSearchable}
        shortenLabels={shortenLabels}
        isInvalid={isInvalid}
        disallowEmptySelection={disallowEmptySelection}
        isDisabled={isDisabled}
        className={className}
        emptyState={emptyState}
        listBoxContent={listBoxContent}
      />
    );
  }

  const handleSingleSelect = (key: string | number | null) => {
    if (key !== null) {
      onSelectionChange?.([String(key)]);
    }
  };

  // One ListBox instance, rendered bare or wrapped in Autocomplete when the search
  // field shows. `listRef` measures overflow whenever isSearchable, so a fitting
  // list can still switch search on if it later grows past the dropdown.
  const list = (
    <ListBox
      ref={isSearchable ? listRef : undefined}
      items={kinds}
      className={styles.listBox}
      renderEmptyState={() => emptyState}
    >
      {listBoxContent}
    </ListBox>
  );

  return (
    <div className={clsx(styles.root, className)}>
      {renderLabel ? renderLabel(triggerId) : null}
      <Select
        selectedKey={selectedKeys?.[0] ?? null}
        onSelectionChange={handleSingleSelect}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        placeholder={placeholder}
        aria-label={resolvedAriaLabel}
      >
        {!renderLabel && label ? <Label>{label}</Label> : null}
        <Button id={triggerId} className={clsx(styles.trigger, isInvalid && styles.invalid)}>
          <SelectValue className={styles.triggerText}>
            {({ isPlaceholder }) => {
              if (isPlaceholder) {
                return placeholder;
              }
              const selectedKey = selectedKeys?.[0];
              const kind = selectedKey ? byKey.get(selectedKey) : undefined;
              return kind?.name || kind?.key || selectedKey || '';
            }}
          </SelectValue>
          <Icon name="chevron-down" size="small" />
        </Button>
        <Popover className={styles.popover}>
          {showSearch ? (
            <Autocomplete filter={filterByText}>
              <SearchField aria-label="Search context kinds" className={styles.search} autoFocus>
                <Input placeholder="Search context kinds" />
              </SearchField>
              {list}
            </Autocomplete>
          ) : (
            list
          )}
        </Popover>
      </Select>
    </div>
  );
};

// Show at most this many selected labels in the trigger; the rest collapse into a
// `+N more` badge. Matches `MultiSelectWithSearch`'s default.
const MAX_DISPLAYED_OPTIONS = 2;

/**
 * Multi-select variant uses DialogTrigger + ListBox because Select only supports
 * single selection. The trigger shows the first `MAX_DISPLAYED_OPTIONS` selected
 * labels with a `+N more` badge for the rest, mirroring `MultiSelectWithSearch`.
 */
function MultiSelectContextKindSelector({
  kinds,
  byKey,
  selectedKeys,
  onSelectionChange,
  label,
  renderLabel,
  ariaLabel,
  triggerId,
  placeholder,
  isSearchable,
  shortenLabels,
  isInvalid,
  disallowEmptySelection,
  isDisabled,
  className,
  emptyState,
  listBoxContent,
}: {
  kinds: ContextKind[];
  byKey: Map<string, ContextKind>;
  selectedKeys?: string[];
  onSelectionChange?: (selectedKeys: string[]) => void;
  label?: string;
  renderLabel?: (triggerId: string) => ReactNode;
  ariaLabel?: string;
  triggerId: string;
  placeholder: string;
  isSearchable: boolean;
  shortenLabels: boolean;
  isInvalid?: boolean;
  disallowEmptySelection?: boolean;
  isDisabled?: boolean;
  className?: string;
  emptyState: React.ReactNode;
  listBoxContent: (contextKind: ContextKind) => React.ReactNode;
}) {
  const handleMultiSelect = (keys: 'all' | Set<string | number>) => {
    if (keys === 'all') {
      onSelectionChange?.(kinds.map((k) => k.key));
      return;
    }
    onSelectionChange?.(Array.from(keys, (key) => String(key)));
  };

  const selectedNames = useMemo(
    () => (selectedKeys ?? []).map((key) => byKey.get(key)?.name || byKey.get(key)?.key || key),
    [selectedKeys, byKey],
  );
  const overflowCount = selectedNames.length - MAX_DISPLAYED_OPTIONS;
  // Only offer search once the list overflows the dropdown (see single-select).
  const { ref: listRef, overflows } = useListOverflow(kinds.length);
  const showSearch = isSearchable && overflows;

  // Trigger text: the first `MAX_DISPLAYED_OPTIONS` selected labels, comma-joined.
  // When `shortenLabels` is set, long individual labels are truncated once the joined
  // string gets long — same thresholds as `MultiSelectWithSearch`.
  const getTriggerLabel = () => {
    if (selectedNames.length === 0) {
      return placeholder;
    }
    const displayedLabels = selectedNames.slice(0, MAX_DISPLAYED_OPTIONS);
    const joined = displayedLabels.join(', ');

    if (shortenLabels && joined.length > 20) {
      const truncateAtLength = 12;
      return displayedLabels
        .map((text) => (text.length > truncateAtLength ? `${text.substring(0, truncateAtLength).trim()}...` : text))
        .join(', ');
    }
    return joined;
  };

  // One ListBox instance, rendered bare or wrapped in Autocomplete when the search
  // field shows (see the single-select branch).
  const list = (
    <ListBox
      ref={isSearchable ? listRef : undefined}
      aria-label={ariaLabel ?? 'Context kinds'}
      items={kinds}
      selectionMode="multiple"
      selectedKeys={new Set(selectedKeys ?? [])}
      onSelectionChange={handleMultiSelect}
      disallowEmptySelection={disallowEmptySelection}
      className={styles.listBox}
      renderEmptyState={() => emptyState}
    >
      {listBoxContent}
    </ListBox>
  );

  return (
    <div className={clsx(styles.root, className)}>
      {renderLabel ? renderLabel(triggerId) : label ? <Label>{label}</Label> : null}
      <DialogTrigger>
        <Button
          id={triggerId}
          className={clsx(styles.trigger, isInvalid && styles.invalid)}
          isDisabled={isDisabled}
          aria-label={renderLabel ? undefined : ariaLabel}
        >
          <span className={styles.triggerText}>{getTriggerLabel()}</span>
          {overflowCount > 0 && <span className={styles.moreOptions}>{`+${overflowCount} more`}</span>}
          <Icon name="chevron-down" size="small" />
        </Button>
        <Popover className={styles.popover}>
          {showSearch ? (
            <Autocomplete filter={filterByText}>
              <SearchField aria-label="Search context kinds" className={styles.search} autoFocus>
                <Input placeholder="Search context kinds" />
              </SearchField>
              {list}
            </Autocomplete>
          ) : (
            list
          )}
        </Popover>
      </DialogTrigger>
    </div>
  );
}
